import type { SortField, TaskListQuery, TaskViewMode } from '@taskos/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Save, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { errorMessage, patch, post } from '../api/client';
import { useTasks } from '../api/queries';
import { useSession } from '../api/session';
import { useToast } from '../components/toast';
import { Button, ErrorState, Field, Modal, Segmented, Skeleton } from '../components/ui';
import { BulkBar } from '../features/tasks/BulkBar';
import { ActiveFilterChips, FilterButton, SortControl, type Filters } from '../features/tasks/Filters';
import { TaskCollection } from '../features/tasks/TaskCollection';
import { useDebounced } from '../lib/hooks';
import { useViewMode, VIEW_OPTIONS } from './Dashboard';

type View = TaskListQuery['view'];

const META: Record<string, { title: string; emoji: string; empty: string; emptyText?: string; sort?: SortField }> = {
  inbox: { title: 'Inbox', emoji: '📥', empty: 'Inbox is empty.', emptyText: 'Capture ideas quickly with Quick Add, then convert them to tasks.', sort: 'created' },
  today: { title: 'Today', emoji: '🎯', empty: 'No tasks due today.', emptyText: 'Enjoy the clear slate.' },
  overdue: { title: 'Overdue', emoji: '🚨', empty: 'Nothing is overdue.', emptyText: 'Great job staying on top of things.', sort: 'priority' },
  followups: { title: 'Follow-ups', emoji: '📌', empty: 'No open follow-ups.' },
  upcoming: { title: 'Upcoming', emoji: '📅', empty: 'Nothing scheduled ahead.' },
  waiting: { title: 'Waiting', emoji: '⏳', empty: 'Nothing is waiting on others.' },
  recurring: { title: 'Recurring', emoji: '🔁', empty: 'No recurring tasks yet.', emptyText: 'Create one with the 🔁 Recurring task type.' },
  mine: { title: 'Assigned to me', emoji: '🙋', empty: 'Nothing assigned to you.' },
  team: { title: 'Team tasks', emoji: '👥', empty: 'No open staff tasks.' },
  all: { title: 'All tasks', emoji: '📋', empty: 'No tasks match.' },
  history: { title: 'Closed tasks', emoji: '📊', empty: 'No closed tasks yet.', sort: 'updated' },
};

const ARRAY_KEYS = ['priority', 'status', 'taskType', 'visibility', 'assigneeId', 'createdById', 'projectId', 'categoryId', 'tag'] as const;
const PAGE = 50;
const MAX_RENDERED = 200;

/** Filters/sort/search live in the URL so back/forward and deep links keep the exact state. */
function readFilters(sp: URLSearchParams): Filters {
  const f: Record<string, unknown> = {};
  for (const k of ARRAY_KEYS) {
    const v = sp.getAll(k);
    if (v.length) f[k] = v;
  }
  for (const k of ['dueFrom', 'dueTo', 'createdFrom', 'createdTo'] as const) if (sp.get(k)) f[k] = sp.get(k);
  if (sp.get('includeClosed') === 'true') f.includeClosed = true;
  return f as Filters;
}

export function TaskListPage({ view, initial, title: titleOverride }: { view: View; initial?: Partial<TaskListQuery>; title?: string }) {
  const [sp, setSp] = useSearchParams();
  const { me } = useSession();
  const [mode, setMode] = useViewMode();
  const meta = META[view] ?? META.all!;
  const filters = useMemo(() => ({ ...(initial as Filters | undefined), ...readFilters(sp) }), [sp, initial]);
  const sort = (sp.get('sort') as SortField) ?? initial?.sort ?? meta.sort ?? me.preferences.sort.field;
  const dir = (sp.get('dir') as 'asc' | 'desc') ?? initial?.dir ?? (meta.sort === 'updated' || meta.sort === 'created' ? 'desc' : me.preferences.sort.dir);
  const [q, setQ] = useState(sp.get('q') ?? initial?.q ?? '');
  const dq = useDebounced(q.trim(), 300);
  const [limit, setLimit] = useState(PAGE);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saveOpen, setSaveOpen] = useState(false);

  useEffect(() => {
    const next = new URLSearchParams(sp);
    if (dq) next.set('q', dq);
    else next.delete('q');
    if (next.toString() !== sp.toString()) setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq]);
  useEffect(() => {
    setLimit(PAGE);
    setSelected(new Set());
  }, [view, sp]);

  const setFilters = (f: Filters) => {
    const next = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) {
      if (Array.isArray(v)) v.forEach((x) => next.append(k, String(x)));
      else if (v !== undefined && v !== '' && v !== false) next.set(k, String(v));
    }
    if (sp.get('sort')) next.set('sort', sp.get('sort')!);
    if (sp.get('dir')) next.set('dir', sp.get('dir')!);
    if (dq) next.set('q', dq);
    setSp(next);
  };
  const setSort = (s: SortField, d: 'asc' | 'desc') => {
    const next = new URLSearchParams(sp);
    next.set('sort', s);
    next.set('dir', d);
    setSp(next, { replace: true });
    void patch('/api/me/preferences', { sort: { field: s, dir: d } }).catch(() => undefined);
  };

  const query: Partial<TaskListQuery> = { ...filters, view, q: dq || undefined, sort, dir, limit: Math.min(limit, MAX_RENDERED) };
  const res = useTasks(query);
  const items = res.data?.items ?? [];
  const total = res.data?.total ?? 0;

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>
            <span aria-hidden>{meta.emoji}</span> {titleOverride ?? meta.title}
          </h1>
          <p className="sub" aria-live="polite">
            {res.isSuccess ? `${total} task${total === 1 ? '' : 's'}` : ' '}
          </p>
        </div>
        <Segmented label="View" value={mode} onChange={(m: TaskViewMode) => setMode(m)} options={VIEW_OPTIONS} />
      </div>
      <div className="toolbar">
        <div className="search-box">
          <Search size={16} aria-hidden />
          <input className="input" placeholder={`Search ${meta.title.toLowerCase()}…`} value={q} onChange={(e) => setQ(e.target.value)} aria-label={`Search ${meta.title}`} />
          {q && (
            <button type="button" className="link-btn" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }} onClick={() => setQ('')} aria-label="Clear search">
              <X size={16} />
            </button>
          )}
        </div>
        <FilterButton f={filters} onChange={setFilters} />
        <SortControl sort={sort} dir={dir} onChange={setSort} />
        <Button icon={<Save size={16} />} onClick={() => setSaveOpen(true)} title="Save this view">
          <span className="hide-mobile">Save view</span>
        </Button>
        {mode === 'LIST' && items.length > 0 && (
          <Button
            onClick={() => setSelected((s) => (s.size === items.length ? new Set() : new Set(items.map((t) => t.id))))}
            aria-pressed={selected.size > 0 && selected.size === items.length}
          >
            {selected.size && selected.size === items.length ? 'Select none' : 'Select all'}
          </Button>
        )}
      </div>
      <ActiveFilterChips f={filters} onChange={setFilters} />
      {res.isError ? (
        <ErrorState error={res.error} onRetry={() => res.refetch()} />
      ) : res.isPending ? (
        <Skeleton rows={6} />
      ) : (
        <>
          <TaskCollection
            items={items}
            mode={mode}
            selectable={mode === 'LIST'}
            selected={selected}
            onToggleSelect={toggle}
            emptyTitle={dq ? `No results for “${dq}”` : meta.empty}
            emptyIcon={dq ? '🔎' : meta.emoji}
            emptyText={dq ? 'Try fewer or different words.' : meta.emptyText}
            baseQuery={query}
          />
          {mode !== 'CALENDAR' && total > items.length && (
            <div className="inline-row mt" style={{ justifyContent: 'center' }}>
              {items.length < MAX_RENDERED ? (
                <Button onClick={() => setLimit((l) => l + PAGE)} loading={res.isFetching}>
                  Load more ({total - items.length} more)
                </Button>
              ) : (
                <span className="muted small">Showing the first {MAX_RENDERED} of {total}. Use search or filters to narrow the list.</span>
              )}
            </div>
          )}
          {view === 'inbox' && items.length > 0 && <p className="muted small mt">Tip: open an inbox item and press “Convert to task” to plan it.</p>}
        </>
      )}
      <BulkBar ids={[...selected]} onClear={() => setSelected(new Set())} />
      <SaveViewDialog open={saveOpen} onClose={() => setSaveOpen(false)} query={{ ...filters, view, q: dq || undefined, sort, dir }} mode={mode} />
    </>
  );
}

function SaveViewDialog({ open, onClose, query, mode }: { open: boolean; onClose: () => void; query: Partial<TaskListQuery>; mode: TaskViewMode }) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Save view"
      size="narrow"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!name.trim()}
            onClick={async () => {
              setError(null);
              try {
                await post('/api/views', { name: name.trim(), query, viewMode: mode });
                toast.push({ kind: 'success', title: `Saved view “${name.trim()}”` });
                void qc.invalidateQueries({ queryKey: ['views'] });
                setName('');
                onClose();
              } catch (e) {
                setError(errorMessage(e));
              }
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <Field label="Name" htmlFor="sv-name" error={error}>
        <input id="sv-name" className="input" value={name} maxLength={60} placeholder="e.g. My high priority" onChange={(e) => setName(e.target.value)} />
      </Field>
      <p className="hint mt">The current filters, search, sort and layout are saved.</p>
    </Modal>
  );
}
